import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { useStripeCardPayment } from '@/composables/payment/useStripeCardPayment'


export default /*#__PURE__*/_defineComponent({
  __name: 'CardPayment',
  props: {
  onValidationChange: { type: Function, required: true },
  isSubscription: { type: Boolean, default: false },
  amount: { type: Number, default: 550 },
  selectedIndex: { type: Number, default: 1 },
  payment_method_used: { type: String, default: '' },
  print_type: { type: String, default: '' },
  format_id: { type: String, default: '' },
  notificationSetting: { type: String, default: 'on' },
  paymentMode: {
    type: String,
    default: 'eprint',
    validator: (v: string) => ['eprint', 'quickemail'].indexOf(v) !== -1
  },
  quickemailFormData: {
    type: Object,
    default: () => ({})
  }
},
  emits: ["payment-success", "payment-error"],
  setup(__props, { expose, emit }: { emit: ({
  (e: 'payment-success', payload: unknown): void
  (e: 'payment-error', payload: { message: string }): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

/**
 * Stripe Elements カード入力。決済手続きは useStripeCardPayment。
 */




// Stripe Elements のマウント先は「自分の要素」を渡す。id セレクタ渡しにすると、
// 同一ページに CardPayment が複数マウントされたとき（ヤギプラ課金ダイアログ等）
// document 内で最初の要素に無音で誤マウントする
const cardNumberEl = ref<HTMLElement | null>(null)
const cardExpiryEl = ref<HTMLElement | null>(null)
const cardCvcEl = ref<HTMLElement | null>(null)
const cardErrorEl = ref<HTMLElement | null>(null)

const stripe = ref<any>(null)
const elements = ref<any>(null)
const cardNumber = ref<any>(null)
const cardExpiry = ref<any>(null)
const cardCvc = ref<any>(null)
const isCardNumberComplete = ref(false)
const isCardExpiryComplete = ref(false)
const isCardCvcComplete = ref(false)
const isLoading = ref(false)
const isProcessing = ref(false)
const isAlreadySubscribed = ref(false)
const paymentIntent = ref<Record<string, unknown> | null>(null)
const quickemailHistoryId = ref<number | null>(null)

const isFormValid = computed(
  () =>
    isCardNumberComplete.value &&
    isCardExpiryComplete.value &&
    isCardCvcComplete.value
)

watch(isFormValid, (newValue) => {
  props.onValidationChange(newValue)
})

const api = useStripeCardPayment({
  isSubscription: toRef(props, 'isSubscription'),
  amount: toRef(props, 'amount'),
  selectedIndex: toRef(props, 'selectedIndex'),
  notificationSetting: toRef(props, 'notificationSetting'),
  paymentMode: toRef(props, 'paymentMode'),
  print_type: toRef(props, 'print_type'),
  format_id: toRef(props, 'format_id'),
  quickemailFormData: toRef(props, 'quickemailFormData') as any,
  isLoading,
  isProcessing,
  isAlreadySubscribed,
  paymentIntent,
  quickemailHistoryId,
  cardNumber,
  stripe,
  emitSuccess: (payload) => emit('payment-success', payload),
  emitError: (payload) => emit('payment-error', payload)
})

function handleCardChange(event: any) {
  if (event.elementType === 'cardNumber') {
    isCardNumberComplete.value = event.complete
  } else if (event.elementType === 'cardExpiry') {
    isCardExpiryComplete.value = event.complete
  } else if (event.elementType === 'cardCvc') {
    isCardCvcComplete.value = event.complete
  }
  const el = cardErrorEl.value
  if (!el) return
  el.innerHTML = event.error ? event.error.message : ''
}

async function createToken() {
  if (isProcessing.value) return
  try {
    const { token, error } = await stripe.value.createToken(cardNumber.value)
    if (error) {
      const el = cardErrorEl.value
      if (el) el.innerHTML = error.message
      emit('payment-error', { message: error.message })
      return
    }
    api.tokenCreated(token)
  } catch (error: any) {
    emit('payment-error', {
      message: error.message || 'トークン作成中にエラーが発生しました'
    })
  }
}

/**
 * サーバが返した client_secret で3Dセキュア認証を実行する。
 * 支払い方法はサーバ側でサブスクに紐付け済みのため、ここでは指定しない。
 */
async function confirmCard3ds(clientSecret: string) {
  if (!stripe.value) {
    return { error: { message: '決済の初期化が完了していません' } }
  }
  return stripe.value.confirmCardPayment(clientSecret)
}

onMounted(async () => {
  try {
    stripe.value = await loadStripe(
      process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || ''
    )
    if (!stripe.value) return
    if (!cardNumberEl.value || !cardExpiryEl.value || !cardCvcEl.value) {
      console.error('カードフォームの要素が見つかりません')
      return
    }
    elements.value = stripe.value.elements()
    const style = { base: { fontSize: '16px' } }
    cardNumber.value = elements.value.create('cardNumber', { style })
    cardNumber.value.mount(cardNumberEl.value)
    cardNumber.value.on('change', handleCardChange)
    cardExpiry.value = elements.value.create('cardExpiry', { style })
    cardExpiry.value.mount(cardExpiryEl.value)
    cardExpiry.value.on('change', handleCardChange)
    cardCvc.value = elements.value.create('cardCvc', { style })
    cardCvc.value.mount(cardCvcEl.value)
    cardCvc.value.on('change', handleCardChange)
  } catch (error) {
    console.error('初期化中にエラーが発生しました:', error)
  }
})

onBeforeUnmount(() => {
  if (cardNumber.value) cardNumber.value.destroy()
  if (cardExpiry.value) cardExpiry.value.destroy()
  if (cardCvc.value) cardCvc.value.destroy()
})

expose({
  createToken,
  confirmCard3ds,
  createQuickmailIntent: api.createQuickmailIntent,
  createPaymentIntent: api.createPaymentIntent,
  savePaymentLogWithStripeId: api.savePaymentLogWithStripeId,
  paymentMode: toRef(props, 'paymentMode'),
  quickemailHistoryId
})

return { __sfc: true,props, emit, cardNumberEl, cardExpiryEl, cardCvcEl, cardErrorEl, stripe, elements, cardNumber, cardExpiry, cardCvc, isCardNumberComplete, isCardExpiryComplete, isCardCvcComplete, isLoading, isProcessing, isAlreadySubscribed, paymentIntent, quickemailHistoryId, isFormValid, api, handleCardChange, createToken, confirmCard3ds }
}

})